'use client';

import { CopyIcon, ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
import type { UIMessage } from 'ai';
import { Fragment, memo, useCallback, useMemo } from 'react';

import { cn } from '@/lib/utils';
import { TooltipProvider } from '@/shared/ui/shadcn/tooltip';

import {
  buildMessageTimeline,
  getPendingStatus,
  getToolStepActivity,
  renderMessageParts,
  isTextPart,
  type TimelineStep,
  type ToolPartRenderer,
} from '../../model';
import { useCopilotToolUiOptional } from '../ToolUi/useCopilotToolUi';
import {
  Conversation,
  ConversationContent,
  ConversationFollowOnSend,
  ConversationScrollButton,
} from '../Conversation/Conversation';
import {
  Message,
  MessageContent,
  MessageActions,
  MessageAction,
  MessageToolbar,
} from '../Message/Message';
import { StatusLine } from '../StatusLine/StatusLine';
import { Reasoning, ReasoningContent, ReasoningTrigger } from '../Reasoning';

type CopilotChatStatus = 'ready' | 'submitted' | 'streaming' | 'error';

export interface CopilotThreadProps {
  /** UIMessage stream from AI SDK. */
  messages: UIMessage[];
  /** AI SDK status (used for optimistic assistant placeholder behavior) */
  status?: CopilotChatStatus;
  /** Stream failure to show in place of the reply that never arrived. */
  error?: Error;
  toolPartRenderers?: Readonly<Record<string, ToolPartRenderer>>;
  /** Callback when copy action is clicked */
  onCopy?: (messageId: string, content: string) => void;
  /** Callback when thumbs up is clicked */
  onThumbsUp?: (messageId: string) => void;
  /** Callback when thumbs down is clicked */
  onThumbsDown?: (messageId: string) => void;
  /** Additional className for the container */
  className?: string;
}

interface CopilotMessageRowProps {
  messageId: string;
  role: UIMessage['role'];
  parts: UIMessage['parts'];
  isLastAssistantMessage: boolean;
  isBusy: boolean;
  toolPartRenderers?: Readonly<Record<string, ToolPartRenderer>>;
  onCopyMessageText: (messageId: string, parts: UIMessage['parts']) => void;
  onThumbsUp: (messageId: string) => void;
  onThumbsDown: (messageId: string) => void;
}

interface AssistantTimelineProps {
  messageId: string;
  parts: UIMessage['parts'];
  isNarrating: boolean;
  toolPartRenderers?: Readonly<Record<string, ToolPartRenderer>>;
}

/** Reasoning, tool steps and text in the order they streamed, so the turn reads as it happened. */
function AssistantTimeline({
  messageId,
  parts,
  isNarrating,
  toolPartRenderers,
}: AssistantTimelineProps) {
  const steps = useMemo(() => buildMessageTimeline(parts), [parts]);
  const pendingStatus = isNarrating ? getPendingStatus(steps) : null;

  const renderStep = (step: TimelineStep, isLast: boolean) => {
    const partsKey = `${messageId}-${step.key}`;
    switch (step.kind) {
      case 'reasoning':
        return (
          <Reasoning key={step.key} isStreaming={isNarrating && isLast}>
            <ReasoningTrigger />
            <ReasoningContent>{step.text}</ReasoningContent>
          </Reasoning>
        );
      case 'tool': {
        const activity = getToolStepActivity(step, isNarrating);
        return (
          <Fragment key={step.key}>
            {activity ? <StatusLine text={activity.label} isActive={activity.isRunning} /> : null}
            {activity && toolPartRenderers?.[step.toolName] ? (
              <MessageContent>
                {renderMessageParts(activity.parts, partsKey, { toolPartRenderers })}
              </MessageContent>
            ) : null}
          </Fragment>
        );
      }
      case 'text':
        return (
          <MessageContent key={step.key}>
            {renderMessageParts(step.parts, partsKey, { toolPartRenderers })}
          </MessageContent>
        );
    }
  };

  return (
    <>
      {steps.map((step, index) => renderStep(step, index === steps.length - 1))}
      {pendingStatus ? <StatusLine text={pendingStatus} /> : null}
    </>
  );
}

const CopilotMessageRow = memo(function CopilotMessageRow({
  messageId,
  role,
  parts,
  isLastAssistantMessage,
  isBusy,
  toolPartRenderers,
  onCopyMessageText,
  onThumbsUp,
  onThumbsDown,
}: CopilotMessageRowProps) {
  const isNarrating = role === 'assistant' && isLastAssistantMessage && isBusy;

  return (
    <div className={cn(role === 'user' && 'mt-4 first:mt-0')}>
      <Message from={role}>
        {role === 'assistant' ? (
          <AssistantTimeline
            messageId={messageId}
            parts={parts}
            isNarrating={isNarrating}
            toolPartRenderers={toolPartRenderers}
          />
        ) : (
          <MessageContent>
            {renderMessageParts(parts, messageId, { toolPartRenderers })}
          </MessageContent>
        )}

        {role === 'assistant' && isLastAssistantMessage && !isBusy && (
          <MessageToolbar>
            <MessageActions>
              <MessageAction tooltip="Copy" onClick={() => onCopyMessageText(messageId, parts)}>
                <CopyIcon className="size-4" />
              </MessageAction>
              <MessageAction tooltip="Good response" onClick={() => onThumbsUp(messageId)}>
                <ThumbsUpIcon className="size-4" />
              </MessageAction>
              <MessageAction tooltip="Bad response" onClick={() => onThumbsDown(messageId)}>
                <ThumbsDownIcon className="size-4" />
              </MessageAction>
            </MessageActions>
          </MessageToolbar>
        )}
      </Message>
    </div>
  );
});

/**
 * CopilotThread — renders a complete AI conversation with messages,
 * reasoning blocks, tool calls, and follow-up suggestions.
 *
 * This is the main conversational UI component for the copilot feature.
 * It handles all the complexity of rendering different message types
 * internally, exposing only high-level callbacks.
 *
 * @example
 * ```tsx
 * <CopilotThread
 *   messages={messages}
 *   onCopy={(id, text) => console.log('Copied:', text)}
 * />
 * ```
 */
export function CopilotThread({
  messages,
  status = 'ready',
  error,
  toolPartRenderers,
  onCopy,
  onThumbsUp,
  onThumbsDown,
  className,
}: CopilotThreadProps) {
  const isBusy = status === 'submitted' || status === 'streaming';
  const toolUi = useCopilotToolUiOptional();
  const resolvedToolPartRenderers = toolPartRenderers ?? toolUi?.toolPartRenderers;
  const handleCopy = useCallback(
    (messageId: string, content: string) => {
      onCopy?.(messageId, content);
      // Default behavior: copy to clipboard
      if (!onCopy) {
        navigator.clipboard.writeText(content).catch(console.error);
      }
    },
    [onCopy]
  );

  const handleThumbsUp = useCallback(
    (messageId: string) => {
      onThumbsUp?.(messageId);
      if (!onThumbsUp) {
        console.log('[CopilotThread] Thumbs up:', messageId);
      }
    },
    [onThumbsUp]
  );

  const handleThumbsDown = useCallback(
    (messageId: string) => {
      onThumbsDown?.(messageId);
      if (!onThumbsDown) {
        console.log('[CopilotThread] Thumbs down:', messageId);
      }
    },
    [onThumbsDown]
  );

  /** Extract all text content from message parts */
  const getMessageText = useCallback(
    (parts: UIMessage['parts']): string =>
      parts
        .filter(isTextPart)
        .map((part) => part.text)
        .join(''),
    []
  );

  const lastAssistantMessageId = useMemo(
    () => messages.findLast((message) => message.role === 'assistant')?.id,
    [messages]
  );

  const lastUserMessageId = useMemo(
    () => messages.findLast((message) => message.role === 'user')?.id,
    [messages]
  );

  const lastRealMessage = messages.length > 0 ? messages[messages.length - 1] : undefined;
  const shouldShowOptimisticAssistant =
    (status === 'submitted' || status === 'streaming') && lastRealMessage?.role === 'user';

  const handleCopyMessageText = useCallback(
    (messageId: string, parts: UIMessage['parts']) => {
      handleCopy(messageId, getMessageText(parts));
    },
    [getMessageText, handleCopy]
  );

  return (
    <TooltipProvider>
      <Conversation className={className}>
        <ConversationContent
          style={{
            paddingTop: '16px',
            // Clear the floating composer plus room for the last reply's actions.
            paddingBottom: 'calc(var(--dock-height, 96px) + 24px)',
          }}
        >
          {messages.map((message) => (
            <CopilotMessageRow
              messageId={message.id}
              role={message.role}
              parts={message.parts}
              isLastAssistantMessage={
                !shouldShowOptimisticAssistant &&
                message.role === 'assistant' &&
                message.id === lastAssistantMessageId
              }
              isBusy={isBusy}
              toolPartRenderers={resolvedToolPartRenderers}
              key={message.id}
              onCopyMessageText={handleCopyMessageText}
              onThumbsDown={handleThumbsDown}
              onThumbsUp={handleThumbsUp}
            />
          ))}

          {shouldShowOptimisticAssistant && (
            <div>
              <Message from="assistant">
                <StatusLine text="Thinking…" />
              </Message>
            </div>
          )}

          {error && (
            <Message from="assistant">
              <MessageContent>
                <div className="w-fit rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs text-foreground">
                  {error.message || 'Cortex could not answer. Please try again.'}
                </div>
              </MessageContent>
            </Message>
          )}
        </ConversationContent>
        <ConversationFollowOnSend lastUserMessageId={lastUserMessageId} />
        <ConversationScrollButton />
      </Conversation>
    </TooltipProvider>
  );
}
